import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  VStack,
  Text,
  Card,
  CardHeader,
  CardBody,
  Heading,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Link,
  Image,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import apiService from '@/services/api'
import CoreTechLogo from '@/assets/logo.svg'

interface PasswordData {
  password: string
  passwordConfirmation: string
}

interface PasswordErrors {
  password?: string
  passwordConfirmation?: string
  general?: string
}

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [passwordData, setPasswordData] = useState<PasswordData>({
    password: '',
    passwordConfirmation: '',
  })
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmation: false,
  })
  const [errors, setErrors] = useState<PasswordErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    // Redirect to forgot password if missing required parameters
    if (!token || !email) {
      navigate('/forgot-password', { replace: true })
    }
  }, [token, email, navigate])

  const togglePasswordVisibility = (field: 'password' | 'confirmation') => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const validateForm = (): boolean => {
    const newErrors: PasswordErrors = {}

    if (!passwordData.password) {
      newErrors.password = 'Password is required'
    } else if (passwordData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long'
    }

    if (!passwordData.passwordConfirmation) {
      newErrors.passwordConfirmation = 'Password confirmation is required'
    } else if (passwordData.password !== passwordData.passwordConfirmation) {
      newErrors.passwordConfirmation = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      await apiService.resetPassword({
        token: token!,
        email: email!,
        password: passwordData.password,
        passwordConfirmation: passwordData.passwordConfirmation,
      })

      setIsSuccess(true)
    } catch (error: any) {
      console.error('Password reset failed:', error)

      const errorMessage = error.response?.data?.message || 'Failed to reset password'

      if (error.response?.status === 422) {
        // Handle validation errors
        const validationErrors = error.response?.data?.errors
        if (validationErrors) {
          setErrors({
            password: validationErrors.password?.[0],
            passwordConfirmation: validationErrors.password_confirmation?.[0],
            general: validationErrors.token?.[0] || validationErrors.email?.[0],
          })
        }
      } else {
        setErrors({ general: errorMessage })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof PasswordData, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }))
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  if (isSuccess) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <Container maxW="md" py={8}>
          <VStack spacing={8}>
            <Image src={CoreTechLogo} alt="CoreTech" h="12" />

            <Card w="full" boxShadow="xl" borderRadius="xl">
              <CardHeader textAlign="center" pb={4}>
                <Heading size="lg" color="gray.800">
                  Password Reset Successful
                </Heading>
              </CardHeader>

              <CardBody pt={0}>
                <VStack spacing={6}>
                  <Alert status="success" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>
                      Your password has been successfully reset. You can now sign in with your new
                      password.
                    </AlertDescription>
                  </Alert>

                  <Button
                    as={RouterLink}
                    to="/login"
                    colorScheme="brand"
                    size="lg"
                    w="full"
                    borderRadius="lg"
                  >
                    Sign In
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </Container>
      </Box>
    )
  }

  return (
    <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
      <Container maxW="md" py={8}>
        <VStack spacing={8}>
          <Image src={CoreTechLogo} alt="CoreTech" h="12" />

          <Card w="full" boxShadow="xl" borderRadius="xl">
            <CardHeader textAlign="center" pb={4}>
              <Heading size="lg" color="gray.800">
                Reset Your Password
              </Heading>
              <Text mt={2} fontSize="sm" color="gray.600">
                Enter your new password below.
              </Text>
            </CardHeader>

            <CardBody pt={0}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6}>
                  {errors.general && (
                    <Alert status="error" borderRadius="lg">
                      <AlertIcon />
                      <AlertDescription>{errors.general}</AlertDescription>
                    </Alert>
                  )}

                  <FormControl isInvalid={!!errors.password}>
                    <FormLabel>New Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showPasswords.password ? 'text' : 'password'}
                        value={passwordData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Enter your new password"
                        size="lg"
                        borderRadius="lg"
                        bg="white"
                        _focus={{
                          borderColor: 'brand.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                        }}
                      />
                      <InputRightElement height="3rem">
                        <IconButton
                          icon={showPasswords.password ? <FiEyeOff /> : <FiEye />}
                          variant="ghost"
                          size="sm"
                          aria-label={showPasswords.password ? 'Hide password' : 'Show password'}
                          onClick={() => togglePasswordVisibility('password')}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{errors.password}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.passwordConfirmation}>
                    <FormLabel>Confirm New Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showPasswords.confirmation ? 'text' : 'password'}
                        value={passwordData.passwordConfirmation}
                        onChange={(e) => handleInputChange('passwordConfirmation', e.target.value)}
                        placeholder="Confirm your new password"
                        size="lg"
                        borderRadius="lg"
                        bg="white"
                        _focus={{
                          borderColor: 'brand.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                        }}
                      />
                      <InputRightElement height="3rem">
                        <IconButton
                          icon={showPasswords.confirmation ? <FiEyeOff /> : <FiEye />}
                          variant="ghost"
                          size="sm"
                          aria-label={
                            showPasswords.confirmation ? 'Hide password' : 'Show password'
                          }
                          onClick={() => togglePasswordVisibility('confirmation')}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{errors.passwordConfirmation}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="brand"
                    size="lg"
                    w="full"
                    borderRadius="lg"
                    isLoading={isLoading}
                    loadingText="Resetting Password..."
                  >
                    Reset Password
                  </Button>

                  <Link
                    as={RouterLink}
                    to="/login"
                    fontSize="sm"
                    color="gray.600"
                    _hover={{ color: 'brand.500' }}
                  >
                    Back to Sign In
                  </Link>
                </VStack>
              </form>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  )
}
